import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import React, { useState, useEffect, createContext, useContext, lazy, Suspense } from "react";
import { Toaster } from "./components/ui/sonner";
import { LanguageProvider } from "./i18n/LanguageProvider";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import ProjekteReferenz from "./pages/ProjekteReferenz";
import ProjektDetail from "./pages/ProjektDetail";
import KontaktPage from "./pages/KontaktPage";
import ImpressumPage from "./pages/ImpressumPage";
import DatenschutzPage from "./pages/DatenschutzPage";
import GlobalDashboard from "./pages/GlobalDashboard";
import ProjectsModule from "./modules/projects/ProjectsModule";
import DailyOverview from "./pages/DailyOverview";
// Stundenzettel removed — funktion integriert in MobileStundenkonto (Inline-Edit)
import TimeClock from "./pages/TimeClock";
import UserManagement from "./pages/UserManagement";
import MitarbeiterDetail from "./pages/MitarbeiterDetail";
import TGAoneProjectDashboard from "./pages/TGAoneProjectDashboard";
import TaskManagement from "./pages/TaskManagement";
import DailyReports from "./pages/DailyReports";
import BautagesberichtForm from "./pages/BautagesberichtForm";
import Einsatzplanung from "./pages/Einsatzplanung";
import Terminplaner from "./pages/Terminplaner";
import Stundenkonten from "./pages/Stundenkonten";
import StundenkontoUebersicht from "./pages/StundenkontoUebersicht";
import Wochenansicht from "./pages/Wochenansicht";
import Monatsansicht from "./pages/Monatsansicht";
import MultiProjektBautagesbericht from "./pages/MultiProjektBautagesbericht";
import AppSettingsPage from "./pages/AppSettingsPage";
import AbrechnungenList from "./pages/AbrechnungenList";
import AbrechnungDetail from "./pages/AbrechnungDetail";
import ProjectControlling from "./pages/ProjectControlling";
import ProjectSettings from "./pages/ProjectSettings";
import WorkerProjectView from "./pages/WorkerProjectView";
const ProjectKalkulation = lazy(() => import("./pages/ProjectKalkulation"));
const ProjectGaebBlock = lazy(() => import("./pages/ProjectGaebBlock"));
const ProjectPlans = lazy(() => import("./pages/ProjectPlans"));
const ProjectSchedulePage = lazy(() => import("./pages/projects/ProjectSchedulePage"));
const PortfolioSchedulePage = lazy(() => import("./pages/projects/PortfolioSchedulePage"));
const BelegPilot = lazy(() => import("./pages/BelegPilot"));
const NinaStudio = lazy(() => import("./pages/NinaStudio"));
const NinaDesignPreview = lazy(() => import("./pages/NinaDesignPreview"));
const NinaIntegrationSettings = lazy(() => import("./pages/NinaIntegrationSettings"));
const NinaDriveMapping = lazy(() => import("./pages/NinaDriveMapping"));
const NinaPublicPage = lazy(() => import("./pages/NinaPublicPage"));
const NinaGuestInvites = lazy(() => import("./pages/NinaGuestInvites"));
const NinaVisualsAdmin = lazy(() => import("./pages/NinaVisualsAdmin"));
const SharedPlanPage = lazy(() => import("./pages/SharedPlanPage"));
import ProjectDocuments from "./pages/ProjectDocuments";
const MobileMyTasks = lazy(() => import("./pages/mobile/MobileMyTasks"));
const TasksOverview = lazy(() => import("./pages/TasksOverview"));
import WorkspaceDashboard from "./pages/WorkspaceDashboard";
import CrmPage from "./pages/CrmPage";
import Urlaubsverwaltung from "./pages/Urlaubsverwaltung";
import ScannerPage from "./pages/ScannerPage";
import GpsAuditLog from "./pages/GpsAuditLog";
import GAEBModule from "./pages/GAEBModule";
import TeamGroups from "./pages/TeamGroups";
import AdminDashboard from "./pages/AdminDashboard";
import SystemHistorie from "./pages/SystemHistorie";
import MobileSystemHistorie from "./pages/mobile/MobileAuditLog";
import MobileLayout from "./layout/MobileLayout";
// HPS-Anlagencheck (Partnerbereich)
import PartnerLogin from "./pages/partner/PartnerLogin";
import HpsLayout from "./pages/hps/HpsLayout";
import HpsDashboard from "./pages/hps/HpsDashboard";
import HpsObjekte from "./pages/hps/HpsObjekte";
import HpsPruefung from "./pages/hps/HpsPruefung";
import HpsBericht from "./pages/hps/HpsBericht";
import HpsBerichteListe from "./pages/hps/HpsBerichteListe";
import HpsKataloge from "./pages/hps/HpsKataloge";
import MobileEinsatzplanung from "./pages/mobile/MobileEinsatzplanung";
import MobileStundenkonto from "./pages/mobile/MobileStundenkonto";
import MobileEingabe from "./pages/mobile/MobileEingabe";
import MobileTagesuebersicht from "./pages/mobile/MobileTagesuebersicht";
import MobileProjekte from "./pages/mobile/MobileProjekte";
import MobileEmployeeStunden from "./pages/mobile/MobileEmployeeStunden";
import ProjektTools from "./pages/mobile/ProjektTools";
import TorampelSteuerung from "./pages/mobile/TorampelSteuerung";
import MitarbeiterZeitanalyse from "./pages/mobile/MitarbeiterZeitanalyse";
import Kabelrechner from "./pages/mobile/Kabelrechner";
import Pruefprotokolle from "./pages/mobile/Pruefprotokolle";
import PasswordVault from "./pages/PasswordVault";
import SharedVaultPage from "./pages/SharedVaultPage";
import DocumentScanner from "./pages/mobile/DocumentScanner";
import AppLayout from "./layout/AppLayout";
import { ProjectProvider } from "./context/ProjectContext";

const API = '/api';

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const authFetch = async (url, options = {}) => {
  const token = localStorage.getItem("token");
  const isFormData = options.body instanceof FormData;
  const headers = {
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...options.headers,
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  const response = await fetch(url, { ...options, headers });
  if (response.status === 401) {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    window.location.href = "/login";
  }
  return response;
};

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const response = await fetch(`${API}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const text = await response.text();
    let data;
    try {
      data = JSON.parse(text);
    } catch {
      throw new Error("Server nicht erreichbar. Bitte später erneut versuchen.");
    }
    if (!response.ok) {
      throw new Error(data.detail || "Login fehlgeschlagen");
    }
    localStorage.setItem("token", data.token);
    localStorage.setItem("user", JSON.stringify(data.user));
    setUser(data.user);
    // Admin: stiller Nina-Visuals Auto-Sync-Check im Hintergrund (max. 1× pro Session).
    if (data.user?.role === "admin") {
      try {
        const BACKEND = process.env.REACT_APP_BACKEND_URL;
        const flag = sessionStorage.getItem("nina.visuals.autosync.done");
        if (!flag) {
          sessionStorage.setItem("nina.visuals.autosync.done", "1");
          fetch(`${BACKEND}/api/nina-engine/visuals/check-updates`, {
            headers: { Authorization: `Bearer ${data.token}` },
          })
            .then((r) => r.ok ? r.json() : null)
            .then((info) => {
              if (info?.needs_sync) {
                fetch(`${BACKEND}/api/nina-engine/visuals/sync`, {
                  method: "POST",
                  headers: { Authorization: `Bearer ${data.token}` },
                });
              }
            })
            .catch(() => {});
        }
      } catch { /* still im Hintergrund, keine UX-Blockade */ }
    }
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("activeProjectId");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

function LanguageBridge({ children }) {
  const { user } = useAuth();
  return (
    <LanguageProvider userLanguage={user?.language || "de"}>
      {children}
    </LanguageProvider>
  );
}

/**
 * Smart-Redirect für /app/m index:
 * Standard: /app/m/eingabe (Zeiteingabe ist der primäre tägliche Touchpoint)
 * Stempel-User: per mobile_start_screen Einstellung → /app/m/stempeluhr
 */
function MobileStartRedirect() {
  const { user } = useAuth();
  if (user?.mobile_start_screen && typeof user.mobile_start_screen === "string" && user.mobile_start_screen.startsWith("/app/m/")) {
    return <Navigate to={user.mobile_start_screen} replace />;
  }
  return <Navigate to="/app/m/eingabe" replace />;
}

// ===== BUILD-MARKER (Deploy-Verifikation) =====
// eslint-disable-next-line
import { BUILD_LABEL, BUILD_VERSION, BUILD_DATE, BUILD_COMMIT, BUILD_FEATURES } from "./buildInfo";
if (typeof window !== "undefined" && !window.__TGAONE_BUILD_LOGGED__) {
  window.__TGAONE_BUILD_LOGGED__ = true;
  // eslint-disable-next-line no-console
  console.log(
    "%c[TGAone BUILD]%c " + BUILD_LABEL,
    "background:#2563eb;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold",
    "color:#60a5fa;font-family:monospace"
  );
  if (BUILD_FEATURES && BUILD_FEATURES.length) {
    // eslint-disable-next-line no-console
    console.log("[TGAone BUILD] features:", BUILD_FEATURES.join(" | "));
  }
  window.__TGAONE_BUILD__ = { BUILD_LABEL, BUILD_VERSION, BUILD_DATE, BUILD_COMMIT, BUILD_FEATURES };
}

function App() {
  // Einheitliche Single-Domain-Architektur (2026-02, Ronny):
  //   tgaone.de         → öffentliche Website
  //   tgaone.de/nina    → öffentliche Nina (Public + Guest)
  //   tgaone.de/app     → interne TGAone-App (Login-geschützt)
  //   tgaone.de/app/nina→ interne Nina
  // Keine host-aware Weiche, keine `nina.*`-Subdomain — alle Routen laufen
  // gemeinsam durch den Router unten. Public/Guest/Internal-Isolation kommt
  // ausschließlich über Backend-Scopes (Cookie/Token/JWT).
  return (
    <AuthProvider>
      <LanguageBridge>
        <BrowserRouter>
          <ProjectProvider>
          <Routes>
            {/* PUBLIC ROUTES */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/projekte" element={<ProjekteReferenz />} />
            <Route path="/projekte/:slug" element={<ProjektDetail />} />
            <Route path="/kontakt" element={<KontaktPage />} />
            <Route path="/impressum" element={<ImpressumPage />} />
            <Route path="/datenschutz" element={<DatenschutzPage />} />
            <Route path="/partnerlogin" element={<PartnerLogin />} />

            {/* PUBLIC NINA (kein Login, /nina) */}
            <Route path="/nina" element={<NinaPublicPage />} />
            <Route path="/nina/invite/:token" element={<NinaPublicPage />} />
            {/* Backward-Compat: alter Guest-Link-Pfad → neue kanonische URL */}
            <Route path="/invite/:token" element={<Navigate to="/nina" replace />} />

            {/* HPS PARTNERBEREICH (separater Bereich innerhalb /app, eigenes Layout) */}
            <Route
              path="/app/hps-anlagencheck"
              element={
                <ProtectedRoute>
                  <HpsLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<HpsDashboard />} />
              <Route path="objekte" element={<HpsObjekte />} />
              <Route path="pruefung/:inspectionId" element={<HpsPruefung />} />
              <Route path="berichte" element={<HpsBerichteListe />} />
              <Route path="bericht/:reportId" element={<HpsBericht />} />
              <Route path="kataloge" element={<HpsKataloge />} />
            </Route>
            
            {/* PROTECTED APP ROUTES */}
            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              {/* PROJEKTE - DataTable Module */}
              <Route index element={<ProjectsModule />} />
              
              {/* GLOBALE NAVIGATION */}
              <Route path="crm" element={<CrmPage />} />
              <Route path="scanner" element={<ScannerPage />} />
              <Route path="mitarbeiter" element={<UserManagement />} />
              <Route path="mitarbeiter/neu" element={<MitarbeiterDetail />} />
              <Route path="mitarbeiter/:userId" element={<MitarbeiterDetail />} />
              <Route path="mitarbeiter/:userId/zeitanalyse" element={<MitarbeiterZeitanalyse />} />
              <Route path="teams" element={<TeamGroups />} />
              <Route path="live-team" element={<Navigate to="/app" replace />} />
              <Route path="dashboard-admin" element={<AdminDashboard />} />
              <Route path="system-historie" element={<SystemHistorie />} />
              <Route path="einsatzplanung" element={<Einsatzplanung />} />
              <Route path="terminplaner" element={<Terminplaner />} />
              <Route path="stundenkonten" element={<StundenkontoUebersicht />} />
              <Route path="stundenkonten/tabelle" element={<Stundenkonten />} />
              <Route path="wochenansicht" element={<Wochenansicht />} />
              <Route path="monatsansicht" element={<Monatsansicht />} />
              <Route path="bautagesbericht" element={<MultiProjektBautagesbericht />} />
              <Route path="app-einstellungen" element={<AppSettingsPage />} />
              <Route path="abrechnungen" element={<AbrechnungenList />} />
              <Route path="abrechnungen/:abrechnungId" element={<AbrechnungDetail />} />
              <Route path="analytics" element={<Navigate to="/app" replace />} />
              <Route path="gaeb" element={<GAEBModule />} />
              <Route path="urlaub" element={<Urlaubsverwaltung />} />
              <Route path="stempeluhr" element={<TimeClock />} />
              <Route path="tagesuebersicht" element={<Navigate to="/app/stundenkonten" replace />} />{/* Legacy → Stundenkonten */}
              <Route path="stundenzettel" element={<Navigate to="/app/stundenkonten" replace />} />{/* Legacy → Stundenkonten */}
              <Route path="tagesuebersicht-alt" element={<DailyOverview />} />{/* Legacy fallback */}
              <Route path="gps-audit-log" element={<GpsAuditLog />} />
              <Route path="gps-karte" element={<Navigate to="/app" replace />} />
              <Route path="passwort-tresor" element={<PasswordVault />} />
              <Route path="email-zugaenge" element={<Navigate to="/app" replace />} />
              <Route path="casambi" element={<Navigate to="/app" replace />} />
              <Route path="aufgaben" element={<Suspense fallback={<div />}><TasksOverview /></Suspense>} />
              <Route path="belegpilot" element={<Suspense fallback={<div />}><BelegPilot /></Suspense>} />
              <Route path="nina" element={<Suspense fallback={<div />}><NinaStudio /></Suspense>} />
              <Route path="nina/gaeste" element={<Suspense fallback={<div />}><NinaGuestInvites /></Suspense>} />
              <Route path="nina/visuals" element={<Suspense fallback={<div />}><NinaVisualsAdmin /></Suspense>} />
              <Route path="admin/nina-visuals" element={<Navigate to="/app/nina/visuals" replace />} />
              <Route path="system/integrationen/nina-ai" element={<Suspense fallback={<div />}><NinaIntegrationSettings /></Suspense>} />
              <Route path="system/integrationen/drive-mapping" element={<Suspense fallback={<div />}><NinaDriveMapping /></Suspense>} />
              <Route path="design/nina-preview" element={<Suspense fallback={<div />}><NinaDesignPreview /></Suspense>} />
              <Route path="nina-inbox" element={<Navigate to="/app" replace />} />
              
              {/* PROJEKT-KONTEXT NAVIGATION */}
              <Route path="projekte/:projectId/dashboard" element={<TGAoneProjectDashboard />} />
              <Route path="projekte/:projectId/daten" element={<TGAoneProjectDashboard mode="daten" />} />
              <Route path="projekte/:projectId/aufgaben" element={<TaskManagement />} />
              <Route path="projekte/:projectId/berichte" element={<DailyReports />} />
              <Route path="projekte/:projectId/berichte/neu" element={<BautagesberichtForm />} />
              <Route path="projekte/:projectId/berichte/:reportId/bearbeiten" element={<BautagesberichtForm />} />
              <Route path="projekte/:projectId/controlling" element={<ProjectControlling />} />
              <Route path="projekte/:projectId/bauzeitenplan" element={<Suspense fallback={<div />}><ProjectSchedulePage /></Suspense>} />
              <Route path="projekte/:projectId/ablaufplan" element={<Suspense fallback={<div />}><ProjectSchedulePage /></Suspense>} />
              <Route path="projektzeitenplanung" element={<Suspense fallback={<div />}><PortfolioSchedulePage /></Suspense>} />
              <Route path="bauzeitenplan" element={<Navigate to="/app/projektzeitenplanung" replace />} />
              <Route path="projekte" element={<Navigate to="/app" replace />} />{/* Legacy: /app/projekte → Projektliste */}
              <Route path="projekte/:projectId/plaene" element={<Suspense fallback={<div />}><ProjectPlans /></Suspense>} />
              <Route path="projekte/:projectId/einstellungen" element={<ProjectSettings />} />
              <Route path="projekte/:projectId/kalkulation" element={<Suspense fallback={<div />}><ProjectKalkulation /></Suspense>} />
              <Route path="projekte/:projectId/kalkulation/gaeb/:blockId" element={<Suspense fallback={<div />}><ProjectGaebBlock /></Suspense>} />
              <Route path="projekte/:projectId/dokumente" element={<ProjectDocuments />} />
              <Route path="dokumentenablage" element={<Navigate to="/app" replace />} />
              <Route path="email" element={<Navigate to="/app" replace />} />
              <Route path="projekte/:projectId/worker" element={<WorkerProjectView />} />
              <Route path="projekte/:projectId/tools" element={<ProjektTools />} />
              <Route path="projekte/:projectId/tools/torampel" element={<TorampelSteuerung />} />
              <Route path="projekte/:projectId/tools/kabelrechner" element={<Kabelrechner />} />
              <Route path="projekte/:projectId/tools/pruefprotokolle" element={<Pruefprotokolle />} />
              
              {/* WORKSPACE - Mitarbeiter-Bereich (nicht für Admin/Projektleiter) */}
              <Route path="workspace" element={<WorkspaceDashboard />} />
              <Route path="workspace/:projectId" element={<WorkerProjectView />} />
            </Route>

            {/* MOBILE ROUTES - Bottom Navigation Layout */}
            <Route
              path="/app/m"
              element={
                <ProtectedRoute>
                  <MobileLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<MobileStartRedirect />} />
              <Route path="stempeluhr" element={<TimeClock />} />
              <Route path="einsatzplanung" element={<MobileEinsatzplanung />} />
              <Route path="stundenkonto" element={<MobileStundenkonto />} />
              <Route path="eingabe" element={<MobileEingabe />} />
              <Route path="tagesuebersicht" element={<Navigate to="/app/m/eingabe" replace />} />{/* Legacy → Eingabe */}
              <Route path="stundenzettel" element={<Navigate to="/app/m/eingabe" replace />} />{/* Legacy → Eingabe */}
              <Route path="tagesuebersicht-alt" element={<MobileTagesuebersicht />} />{/* Legacy fallback */}
              <Route path="projekte" element={<MobileProjekte />} />
              <Route path="live-team" element={<Navigate to="/app/m/projekte" replace />} />
              <Route path="dashboard-admin" element={<AdminDashboard />} />
              <Route path="system-historie" element={<MobileSystemHistorie />} />
              <Route path="mitarbeiter/:userId/stunden" element={<MobileEmployeeStunden />} />
              <Route path="mitarbeiter/:userId/zeitanalyse" element={<MitarbeiterZeitanalyse />} />
              <Route path="projekt-tools/:projectId" element={<ProjektTools />} />
              <Route path="projekt-tools/:projectId/torampel" element={<TorampelSteuerung />} />
              <Route path="projekt-tools/:projectId/kabelrechner" element={<Kabelrechner />} />
              <Route path="projekt-tools/:projectId/pruefprotokolle" element={<Pruefprotokolle />} />
              <Route path="projekte/:projectId/plaene" element={<Suspense fallback={<div />}><ProjectPlans /></Suspense>} />
              <Route path="passwort-tresor" element={<PasswordVault isMobile={true} />} />
              <Route path="casambi" element={<Navigate to="/app/m/projekte" replace />} />
              <Route path="doc-scanner" element={<DocumentScanner />} />
              <Route path="dokumentenablage" element={<Navigate to="/app/m/projekte" replace />} />
              <Route path="meine-aufgaben" element={<Suspense fallback={<div />}><MobileMyTasks /></Suspense>} />
            </Route>
            
            {/* LEGACY REDIRECTS - alte URLs zur neuen App-Struktur */}
            <Route path="/shared/vault/:token" element={<SharedVaultPage />} />
            <Route path="/share/plan/:token" element={<Suspense fallback={<div />}><SharedPlanPage /></Suspense>} />
            <Route path="/mitarbeiter" element={<Navigate to="/app/mitarbeiter" replace />} />
            <Route path="/einsatzplanung" element={<Navigate to="/app/einsatzplanung" replace />} />
            <Route path="/stundenkonten" element={<Navigate to="/app/stundenkonten" replace />} />
            <Route path="/stempeluhr" element={<Navigate to="/app/stempeluhr" replace />} />
            <Route path="/tagesuebersicht" element={<Navigate to="/app/tagesuebersicht" replace />} />
            <Route path="/users" element={<Navigate to="/app/mitarbeiter" replace />} />
            <Route path="/clock" element={<Navigate to="/app/stempeluhr" replace />} />
            <Route path="/projects" element={<Navigate to="/app" replace />} />
            <Route path="/reports" element={<Navigate to="/app" replace />} />
          </Routes>
        </ProjectProvider>
      </BrowserRouter>
      <Toaster position="top-center" />
      </LanguageBridge>
    </AuthProvider>
  );
}

export default App;
